import { useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Maximize2 } from "lucide-react";
import Lightbox from "./Lightbox";

function FeatureRow({ feat, reversed }) {
  const [lightboxIdx, setLightboxIdx] = useState(null);
  const shots = feat.screenshots || [];
  const open = lightboxIdx !== null;
  const closeLb = useCallback(() => setLightboxIdx(null), []);
  const prev = useCallback(
    () => setLightboxIdx((i) => (i === null ? null : (i - 1 + shots.length) % shots.length)),
    [shots.length]
  );
  const next = useCallback(
    () => setLightboxIdx((i) => (i === null ? null : (i + 1) % shots.length)),
    [shots.length]
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.5, delay: 0.1 }}
      className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center"
    >
      {/* Text */}
      <div className={reversed ? "lg:order-2" : ""}>
        {feat.badge && (
          <span className="inline-block text-[10px] font-mono text-rogue-cyan bg-rogue-cyan/10 px-2 py-1 rounded mb-4 uppercase tracking-wider">
            {feat.badge}
          </span>
        )}
        <h3 className="font-display font-bold text-xl md:text-2xl text-white mb-4 leading-tight">
          {feat.title}
        </h3>
        <p className="text-geminii-muted leading-relaxed mb-6">{feat.description}</p>
        {feat.bullets && (
          <ul className="space-y-3">
            {feat.bullets.map((b, j) => (
              <li key={j} className="flex items-start gap-3">
                <div className="w-1.5 h-1.5 rounded-full bg-rogue-cyan mt-2 flex-shrink-0" />
                <span className="text-sm text-geminii-muted">{b}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Visuals */}
      <div className={reversed ? "lg:order-1" : ""}>
        {/* Custom visual node takes precedence over screenshots */}
        {feat.visual ? (
          feat.visual
        ) : shots.length > 0 ? (
          <div className="space-y-4">
            {/* Primary screenshot */}
            <div
              className="relative group cursor-zoom-in rounded-xl overflow-hidden border border-white/[0.08] shadow-2xl shadow-black/40"
              onClick={() => setLightboxIdx(0)}
              data-testid="feature-screenshot-primary"
            >
              <div className="bg-geminii-card flex items-center gap-2 px-3 py-2 border-b border-white/[0.06]">
                <div className="flex gap-1">
                  <div className="w-2 h-2 rounded-full bg-rogue-rose/50" />
                  <div className="w-2 h-2 rounded-full bg-rogue-amber/50" />
                  <div className="w-2 h-2 rounded-full bg-rogue-emerald/50" />
                </div>
                <span className="text-[9px] font-mono text-geminii-dim ml-1">{shots[0].caption || "rogue.geminii-ai.com"}</span>
              </div>
              <img src={shots[0].src} alt={shots[0].caption || feat.title} className="w-full" />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/25 transition-colors duration-200 flex items-center justify-center pointer-events-none">
                <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur border border-white/20 text-white text-xs font-medium">
                  <Maximize2 size={12} />
                  Expand
                </div>
              </div>
            </div>

            {/* Secondary screenshot */}
            {shots[1] && (
              <div className="flex justify-end -mt-12 mr-0 relative z-10">
                {shots[1].mobile ? (
                  <button
                    onClick={() => setLightboxIdx(1)}
                    className="w-28 rounded-xl overflow-hidden border-2 border-white/[0.1] shadow-xl shadow-black/50 bg-black cursor-zoom-in hover:border-rogue-cyan/40 transition-colors"
                  >
                    <div className="bg-geminii-card h-2.5 flex items-center justify-center">
                      <div className="w-8 h-1 rounded-full bg-white/[0.1]" />
                    </div>
                    <img src={shots[1].src} alt={shots[1].caption || ""} className="w-full" />
                  </button>
                ) : (
                  <button
                    onClick={() => setLightboxIdx(1)}
                    className="w-2/3 rounded-lg overflow-hidden border border-white/[0.1] shadow-xl shadow-black/50 cursor-zoom-in hover:border-rogue-cyan/40 transition-colors text-left"
                  >
                    <div className="bg-geminii-card flex items-center gap-1.5 px-2.5 py-1.5 border-b border-white/[0.06]">
                      <div className="flex gap-1">
                        <div className="w-1.5 h-1.5 rounded-full bg-white/[0.15]" />
                        <div className="w-1.5 h-1.5 rounded-full bg-white/[0.15]" />
                        <div className="w-1.5 h-1.5 rounded-full bg-white/[0.15]" />
                      </div>
                      <span className="text-[8px] font-mono text-geminii-dim ml-1">{shots[1].caption || "Light mode"}</span>
                    </div>
                    <img src={shots[1].src} alt={shots[1].caption || ""} className="w-full" />
                  </button>
                )}
              </div>
            )}
          </div>
        ) : null}
      </div>

      <Lightbox
        open={open}
        items={shots.map((s) => ({ ...s, label: s.caption }))}
        index={lightboxIdx ?? 0}
        onClose={closeLb}
        onPrev={prev}
        onNext={next}
      />
    </motion.div>
  );
}

export default function FeatureShowcase({ features }) {
  return (
    <div className="space-y-20 md:space-y-28">
      {features.map((feat, i) => (
        <FeatureRow key={feat.title} feat={feat} reversed={i % 2 === 1} />
      ))}
    </div>
  );
}
